import { ChangeDetectionStrategy, Component, HostListener, computed, input, output } from '@angular/core';

import type { CustomButtonEventPayload } from '@components/sp-custom-button';
import type { ShadowSettings } from '@common/types/shadow-settings.type';

import type { LoginEduInnerButtonLegacy, LoginEduRegistrationButtonLegacy } from '@sites/entity-core';
import type { LoginEduForm } from '@sites/entity-signal-form';

import { EButtonStyleType } from '@common/enums';
import { isEmptyButtonLabel } from '@common/helpers/forms';
import { ERadioGroupType } from '@ui/sp-radio-group';

import { SpShadowSettingsComponent } from '@components/sp-shadow-settings/sp-shadow-settings.component';
import { WebCustomButtonComponent } from '@web/shared/components/web-custom-button/web-custom-button.component';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

/**
 * Styling panel for the "sign up" button group. Reads and writes
 * `buttons[index]` straight on the widget signal form; `isActive` and
 * `modalTrigger` are owned by the main panel, so patching field-by-field leaves
 * them untouched. The parent control owns the transaction — save/cancel are
 * emitted up.
 *
 * The group renders three different inner buttons depending on the visitor's
 * state (register / enroll / already enrolled). Only the register one is edited
 * here; size and the custom-button theme are mirrored onto the other two, while
 * the theme itself lives on the group root (`buttonBase` / `buttonHover`), not
 * on the inner buttons — same split as the FormGroup version.
 */
@Component({
    selector: 'web-registration-button-settings',
    templateUrl: './registration-button-settings.component.html',
    styleUrls: ['./registration-button-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonElementsModule, SpShadowSettingsComponent, WebCustomButtonComponent],
})
export class RegistrationButtonSettingsComponent {
    public readonly form = input.required<LoginEduForm>();
    public readonly index = input.required<number>();

    public readonly saveEvent = output<void>();
    public readonly cancelEvent = output<void>();

    public readonly EButtonStyleType = EButtonStyleType;
    public readonly ERadioGroupType = ERadioGroupType;

    private readonly button = computed<LoginEduRegistrationButtonLegacy | undefined>(
        () => this.form().buttons().value()[this.index()] as LoginEduRegistrationButtonLegacy | undefined
    );

    public readonly registrationButtonLabel = computed(() => this.button()?.registrationButton?.label ?? '');
    public readonly registrationButtonStyleType = computed(() => this.button()?.registrationButton?.styleType);
    public readonly registrationButtonSize = computed(() => this.button()?.registrationButton?.size);

    /** The group's theme lives on the root, not on the inner register button. */
    public readonly registrationButtonBase = computed(() => this.button()?.buttonBase);
    public readonly registrationButtonHover = computed(() => this.button()?.buttonHover);

    public readonly isIndependentCorners = computed(() => Boolean(this.button()?.isIndependentCorners));
    public readonly advancedRadius = computed(() => this.button()?.advancedRadius);
    public readonly advancedBorderSettings = computed(() => this.button()?.advancedBorderSettings);
    public readonly isShadow = computed(() => Boolean(this.button()?.isShadow));
    public readonly shadowSettings = computed(() => this.button()?.shadowSettings);
    public readonly isCustomStyle = computed(() => this.registrationButtonStyleType() === EButtonStyleType.custom);

    /** Blank button text is invalid — Save and the Enter shortcut are gated on it. */
    public readonly isEmptyLabel = computed(() => isEmptyButtonLabel(this.registrationButtonLabel()));

    // --- Setters (template event handlers) ---

    public onLabelChange(event: Event): void {
        this.patchRegistrationButton({ label: (event.target as HTMLInputElement).value });
    }

    public onStyleTypeChange(event: Event): void {
        this.patchRegistrationButton({
            styleType: (event.target as HTMLSelectElement).value as LoginEduInnerButtonLegacy['styleType'],
        });
    }

    /** Size is shared by all three inner buttons — they render in the same slot. */
    public setRegistrationButtonSize(value: string): void {
        const button = this.button();
        if (!button) return;

        const size = value as LoginEduInnerButtonLegacy['size'];

        this.patch({
            registrationButton: { ...button.registrationButton, size },
            enrollButton: { ...button.enrollButton, size },
            enterButton: { ...button.enterButton, size },
        });
    }

    public setRegistrationButtonBase(changes: CustomButtonEventPayload): void {
        const button = this.button();
        if (!button) return;

        const buttonBase = changes.base as LoginEduRegistrationButtonLegacy['buttonBase'];

        const patch: Partial<LoginEduRegistrationButtonLegacy> = {
            buttonBase,
            buttonHover: changes.hover as LoginEduRegistrationButtonLegacy['buttonHover'],
            // The enroll/enter buttons keep their own copy of the base theme.
            enrollButton: { ...button.enrollButton, buttonBase },
            enterButton: { ...button.enterButton, buttonBase },
        };

        if (changes?.isIndependentCorners !== undefined) {
            patch.isIndependentCorners = changes.isIndependentCorners;
        }
        if (changes?.radiusSettings) {
            patch.advancedRadius = changes.radiusSettings as LoginEduRegistrationButtonLegacy['advancedRadius'];
        }
        if (changes?.advancedBorderSettings) {
            patch.advancedBorderSettings =
                changes.advancedBorderSettings as LoginEduRegistrationButtonLegacy['advancedBorderSettings'];
        }

        this.patch(patch);
    }

    public setShadow(value: boolean): void {
        this.patch({ isShadow: value });
    }

    public setShadowSettings(settings: ShadowSettings): void {
        this.patch({ shadowSettings: settings });
    }

    public onSave(): void {
        this.saveEvent.emit();
    }

    public onReset(): void {
        this.cancelEvent.emit();
    }

    @HostListener('document:keydown.enter')
    public onEnterKeyPress(): void {
        if (this.isEmptyLabel()) return;

        this.onSave();
    }

    @HostListener('document:keydown.escape', ['$event'])
    public onEscapeKeyPress(event: KeyboardEvent): void {
        event.stopPropagation();
        this.onReset();
    }

    private patchRegistrationButton(patch: Partial<LoginEduInnerButtonLegacy>): void {
        const button = this.button();
        if (!button) return;

        this.patch({ registrationButton: { ...button.registrationButton, ...patch } });
    }

    private patch(patch: Partial<LoginEduRegistrationButtonLegacy>): void {
        this.form().updateButton(this.index(), patch);
    }
}
